/**
 * The three readings of one roster period the Profitability page shows:
 * at today's rates, at the rates before the last statutory increase, and with
 * a pay increase that has not happened yet.
 *
 * All three price the same shifts, so the only thing that differs between them
 * is the rates — which is what makes them comparable.
 */

import { latestStatutoryRateChange, statutoryMinimumHourlyRate } from '../payroll/shiftPay';
import { addDaysToDateKey } from '../payroll/time';
import { costRoster, withinPeriod } from './costing';
import { buildMarginReport } from './margins';
import { compareScenario } from './scenario';
import type {
  CostingContext,
  MarginReport,
  PayBasis,
  PayScenario,
  ProfitabilityContract,
  ProfitabilitySettings,
  RosteredShift,
  ScenarioReport,
} from './types';

export interface ProfitabilityPeriod {
  startDate: string;
  endDate: string;
}

export interface ProfitabilityInput {
  /** Every shift of the weeks the period touches: overtime is judged on whole weeks. */
  shifts: RosteredShift[];
  period: ProfitabilityPeriod;
  today: string;
  context: CostingContext;
  contracts: ProfitabilityContract[];
  settings: ProfitabilitySettings;
}

export interface PreviousRates {
  /** The day the statutory minimum last moved. */
  changeDate: string;
  previousRate: number;
  rate: number;
  /** The period's roster at the rates in force the day before. */
  report: MarginReport;
}

export interface ProfitabilityAnalysis {
  /** The period's roster at the rates in force today. */
  current: MarginReport;
  /** null where no statutory minimum is known to have moved for any rostered site. */
  previous: PreviousRates | null;
  scenario: ScenarioReport | null;
}

/** Countries of the sites that have a shift in the period, most shifts first. */
function rosteredCountries(input: ProfitabilityInput): string[] {
  const counts = new Map<string, number>();
  input.shifts.forEach((shift) => {
    if (shift.dateKey < input.period.startDate || shift.dateKey > input.period.endDate) return;
    const country = input.context.sites.get(shift.siteId)?.countryCode;
    if (country) counts.set(country, (counts.get(country) ?? 0) + 1);
  });
  return [...counts.entries()].sort((left, right) => right[1] - left[1]).map(([country]) => country);
}

/**
 * The minimum hourly rate a scenario is measured against: the statutory one
 * for the country most of the roster is in, or the company's base rate where
 * that country has no schedule here.
 */
export function currentMinimumHourlyRate(input: ProfitabilityInput): number {
  const [country] = rosteredCountries(input);
  return (country ? statutoryMinimumHourlyRate(country, input.today) : null)
    ?? input.context.settings.baseHourlyRateEur;
}

function latestChange(input: ProfitabilityInput) {
  return rosteredCountries(input)
    .map((country) => latestStatutoryRateChange(country, input.today))
    .filter((change): change is NonNullable<typeof change> => change !== null)
    .sort((left, right) => right.effectiveFrom.localeCompare(left.effectiveFrom))[0] ?? null;
}

/** The earliest date pay rates have to be known for, so the caller can fetch that far back. */
export function earliestRateDate(today: string, countryCodes: string[]): string {
  const changeDates = countryCodes
    .map((country) => latestStatutoryRateChange(country, today)?.effectiveFrom)
    .filter((date): date is string => Boolean(date));
  return changeDates.length > 0
    ? addDaysToDateKey(changeDates.sort()[0], -1)
    : today;
}

export function analyseProfitability(
  input: ProfitabilityInput,
  scenario: PayScenario | null,
): ProfitabilityAnalysis {
  const { period, contracts, settings } = input;
  const price = (basis: PayBasis): MarginReport => buildMarginReport(
    withinPeriod(costRoster(input.shifts, input.context, basis), period.startDate, period.endDate),
    contracts,
    settings,
  );

  const current = price({ rateDate: input.today, scenario: null });
  const change = latestChange(input);

  return {
    current,
    previous: change ? {
      changeDate: change.effectiveFrom,
      previousRate: change.previousAmount,
      rate: change.amount,
      report: price({ rateDate: addDaysToDateKey(change.effectiveFrom, -1), scenario: null }),
    } : null,
    scenario: scenario
      ? compareScenario(current, price({ rateDate: input.today, scenario }), settings)
      : null,
  };
}
